import type {
  DealResourcePlan,
  DealSow,
  SalesDealEstimateResult,
  SavedDealEstimate,
} from '@protoss/shared';
import * as React from 'react';

import { SowReadinessError } from '../services/salesApi';

// ─── Types ────────────────────────────────────────────────────────────────────

export type PipelineStage = 'idle' | 'active' | 'queued' | 'done' | 'error';

export interface UseDealGenerationReturn {
  // Estimate
  estimateResult: SalesDealEstimateResult | null;
  setEstimateResult: React.Dispatch<React.SetStateAction<SalesDealEstimateResult | null>>;
  loadingEstimate: boolean;
  setLoadingEstimate: React.Dispatch<React.SetStateAction<boolean>>;
  estimateError: string | null;
  setEstimateError: React.Dispatch<React.SetStateAction<string | null>>;
  savedEstimate: SavedDealEstimate | null;
  setSavedEstimate: React.Dispatch<React.SetStateAction<SavedDealEstimate | null>>;
  savingEstimate: boolean;
  setSavingEstimate: React.Dispatch<React.SetStateAction<boolean>>;

  // Resource plan
  resourcePlan: DealResourcePlan | null;
  setResourcePlan: React.Dispatch<React.SetStateAction<DealResourcePlan | null>>;
  loadingResourcePlan: boolean;
  setLoadingResourcePlan: React.Dispatch<React.SetStateAction<boolean>>;
  generatingResourcePlan: boolean;
  setGeneratingResourcePlan: React.Dispatch<React.SetStateAction<boolean>>;
  resourcePlanError: string | null;
  setResourcePlanError: React.Dispatch<React.SetStateAction<string | null>>;

  // SOW
  sow: DealSow | null;
  setSow: React.Dispatch<React.SetStateAction<DealSow | null>>;
  loadingSow: boolean;
  setLoadingSow: React.Dispatch<React.SetStateAction<boolean>>;
  generatingSow: boolean;
  setGeneratingSow: React.Dispatch<React.SetStateAction<boolean>>;
  savingSow: boolean;
  setSavingSow: React.Dispatch<React.SetStateAction<boolean>>;
  sowError: string | null;
  setSowError: React.Dispatch<React.SetStateAction<string | null>>;
  sowReadinessReport: SowReadinessError['readinessReport'] | null;
  setSowReadinessReport: React.Dispatch<
    React.SetStateAction<SowReadinessError['readinessReport'] | null>
  >;
  sowEditing: boolean;
  setSowEditing: React.Dispatch<React.SetStateAction<boolean>>;
  sowDraftContent: Record<string, unknown> | null;
  setSowDraftContent: React.Dispatch<React.SetStateAction<Record<string, unknown> | null>>;

  // Todo drawer visibility state
  todoDismissed: boolean;
  setTodoDismissed: React.Dispatch<React.SetStateAction<boolean>>;

  // Resets all generation state back to initial values
  reset: () => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Manages all AI generation state for the deal page: estimate, resource plan,
 * SOW, loading indicators, error state, and the computed pipeline stages used
 * by GenerationTodoDrawer. Business logic callbacks that depend on late-bound
 * DealPage values (handlePersistAnswers, canGenerateEstimate) live in DealPage
 * itself and use the setters exposed here.
 */
export function useDealGeneration(): UseDealGenerationReturn {
  const [estimateResult, setEstimateResult] = React.useState<SalesDealEstimateResult | null>(null);
  const [loadingEstimate, setLoadingEstimate] = React.useState(false);
  const [estimateError, setEstimateError] = React.useState<string | null>(null);
  const [savedEstimate, setSavedEstimate] = React.useState<SavedDealEstimate | null>(null);
  const [savingEstimate, setSavingEstimate] = React.useState(false);

  const [resourcePlan, setResourcePlan] = React.useState<DealResourcePlan | null>(null);
  const [loadingResourcePlan, setLoadingResourcePlan] = React.useState(false);
  const [generatingResourcePlan, setGeneratingResourcePlan] = React.useState(false);
  const [resourcePlanError, setResourcePlanError] = React.useState<string | null>(null);

  const [sow, setSow] = React.useState<DealSow | null>(null);
  const [loadingSow, setLoadingSow] = React.useState(false);
  const [generatingSow, setGeneratingSow] = React.useState(false);
  const [savingSow, setSavingSow] = React.useState(false);
  const [sowError, setSowError] = React.useState<string | null>(null);
  const [sowReadinessReport, setSowReadinessReport] = React.useState<
    SowReadinessError['readinessReport'] | null
  >(null);
  const [sowEditing, setSowEditing] = React.useState(false);
  const [sowDraftContent, setSowDraftContent] = React.useState<Record<string, unknown> | null>(
    null
  );
  const [todoDismissed, setTodoDismissed] = React.useState(false);

  const reset = React.useCallback(() => {
    setEstimateResult(null);
    setEstimateError(null);
    setLoadingEstimate(false);
    setSavedEstimate(null);
    setSavingEstimate(false);
    setResourcePlan(null);
    setLoadingResourcePlan(false);
    setGeneratingResourcePlan(false);
    setResourcePlanError(null);
    setSow(null);
    setLoadingSow(false);
    setSavingSow(false);
    setSowError(null);
    setSowReadinessReport(null);
    setSowEditing(false);
    setSowDraftContent(null);
  }, []);

  return {
    estimateResult,
    setEstimateResult,
    loadingEstimate,
    setLoadingEstimate,
    estimateError,
    setEstimateError,
    savedEstimate,
    setSavedEstimate,
    savingEstimate,
    setSavingEstimate,

    resourcePlan,
    setResourcePlan,
    loadingResourcePlan,
    setLoadingResourcePlan,
    generatingResourcePlan,
    setGeneratingResourcePlan,
    resourcePlanError,
    setResourcePlanError,

    sow,
    setSow,
    loadingSow,
    setLoadingSow,
    generatingSow,
    setGeneratingSow,
    savingSow,
    setSavingSow,
    sowError,
    setSowError,
    sowReadinessReport,
    setSowReadinessReport,
    sowEditing,
    setSowEditing,
    sowDraftContent,
    setSowDraftContent,

    todoDismissed,
    setTodoDismissed,
    reset,
  };
}
